import { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Pencil,
  Share2,
  Eye,
  Clock,
  MoreHorizontal,
  Trash2,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { DataTable } from "@/components/common/DataTable";
import { buildTableDefaults } from "@/components/common/data-table-helpers";
import { useTran } from "@/hooks";
import { ConfirmDialog } from "@/components/common";
import { useShareSurveyStore } from "../hooks/useShareSurveyStore";
import { formatRelativeTime } from "../utils/survey-manager.helper";

// ── Status Badge ──────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  closed: "bg-gray-100   text-gray-600    border-gray-200",
  draft: "bg-yellow-100 text-yellow-700  border-yellow-200",
  expired: "bg-red-100    text-red-600     border-red-200",
};

const StatusBadge = ({ status }) => (
  <span
    className={cn(
      "inline-flex items-center px-2.5 py-0.5 rounded-full",
      "text-xs font-medium border capitalize",
      STATUS_STYLES[status?.toLowerCase()] || STATUS_STYLES.draft,
    )}
  >
    {status}
  </span>
);
// ── Row Actions ───────────────────────────────────────────────────────────────
const RowActions = ({ survey, onEdit, onPreview, onClose, onDelete }) => {
  const { t } = useTran();
  const shareStore = useShareSurveyStore();
  const [confirmDelete, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isClosed = survey.status === "closed";
  const isActive = survey.status === "active";

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await onDelete?.(survey._id);
    } finally {
      setDeleting(false);
      setConfirm(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          title={t("Edit")}
          onClick={() => onEdit?.(survey)}
        >
          <Pencil className="h-4 w-4 text-gray-400" />
        </Button>
        {/* Inline: Preview */}
        <Button
          variant="ghost"
          size="icon"
          title={t("Preview")}
          onClick={() => onPreview?.(survey)}
        >
          <Eye className="h-4 w-4 text-gray-400" />
        </Button>

        {/* ... Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={t("More actions")}>
              <MoreHorizontal className="h-4 w-4 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-48">
            {/* ✅ Share — calls shareStore.openDialog(survey) */}
            {isActive && (
              <DropdownMenuItem
                onSelect={() => shareStore.openDialog(survey)}
                className="gap-2"
              >
                <Share2 className="h-4 w-4 text-emerald-600" />
                {t("Share survey")}
              </DropdownMenuItem>
            )}

            {/* Close survey */}
            {!isClosed && (
              <DropdownMenuItem
                onSelect={() => onClose?.(survey._id)}
                className="gap-2"
              >
                <XCircle className="h-4 w-4 text-gray-500" />
                {t("Close survey")}
              </DropdownMenuItem>
            )}

            {/* Delete */}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => setConfirm(true)}
              className="gap-2"
              variant="destructive"
            >
              <Trash2 className="h-4 w-4" />
              {t("Delete survey")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={(isOpen) => {
          if (!isOpen) setConfirm(false);
        }}
        title={t("Delete survey?")}
        description={
          <>
            {t("This will permanently delete")} <strong>{survey.name}</strong>{" "}
            {t("and all its submissions.")}
          </>
        }
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        loading={deleting}
        tone="danger"
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

// ── SurveyLibraryTable ────────────────────────────────────────────────────────
const SurveyLibraryTable = ({
  surveys,
  loading,
  sort,
  page,
  pageSize,
  total,
  onSort,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onPreview,
  onClose,
  onShare,
  onDelete,
}) => {
  const { t } = useTran();

  const { emptyMessage, loadingMessage, getRowProps } = buildTableDefaults({
    t,
    emptyMessageKey: "No surveys found.",
  });
  const getExpiryInfo = (survey) => {
    if (!survey.expiresAt || survey.status !== "active") return null; // ← only active
    const now = new Date();
    const expiry = new Date(survey.expiresAt);
    const diffMs = expiry - now;

    if (diffMs <= 0) return null; // already expired, status handles it

    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    return {
      days: diffDays,
      isUrgent: diffDays <= 3, // 🔴 red warning
      isSoon: diffDays <= 7, // 🟡 yellow caution
    };
  };

  const columns = useMemo(
    () => [
      {
        key: "name",
        header: t("Survey Name"),
        render: (survey) => {
          const expiry = getExpiryInfo(survey);

          return (
            <div className="flex flex-col gap-0.5">
              {/* Row 1: name + type badge */}
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {survey.name}
                </span>
                {survey.type && (
                  <Badge
                    variant="outline"
                    className="text-xs font-normal text-gray-500 dark:text-gray-400"
                  >
                    {survey.type}
                  </Badge>
                )}
              </div>

              {/* Row 2: expiry warning */}
              {expiry && (
                <span
                  className={cn(
                    "inline-flex items-center gap-1 text-xs font-medium",
                    expiry.isUrgent
                      ? "text-red-500 dark:text-red-400" // ≤ 3 days
                      : expiry.isSoon
                        ? "text-amber-500 dark:text-amber-400" // ≤ 7 days
                        : "text-gray-400 dark:text-gray-500", // > 7 days
                  )}
                >
                  <Clock className="h-3 w-3" />
                  {expiry.days === 1
                    ? t("Expiring tomorrow")
                    : t("Expiring in {{days}} days", { days: expiry.days })}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: "status",
        header: t("Status"),
        render: (survey) => <StatusBadge status={survey.status} />,
      },
      {
        key: "questions",
        header: t("Questions"),
        render: (survey) => survey.questions?.length ?? 0,
      },
      {
        key: "submissionCount",
        header: t("Submissions"),
        render: (survey) => survey.submissionCount ?? 0,
      },
      {
        key: "completionRate",
        header: t("Completion Rate"),
        render: (survey) =>
          survey.submissionCount > 0 ? `${survey.completionRate ?? 0}%` : "—",
      },
      {
        key: "updatedAt",
        header: t("Last Updated"),
        render: (survey) => {
          if (!survey.updatedAt) return "—";
          try {
            return formatRelativeTime(new Date(survey.updatedAt));
          } catch {
            return "—";
          }
        },
      },
      {
        key: "actions",
        header: t("Actions"),
        render: (survey) => (
          <RowActions
            survey={survey}
            onEdit={onEdit}
            onPreview={onPreview}
            onClose={onClose}
            onDelete={onDelete}
            onShare={onShare}
          />
        ),
      },
    ],
    [t, onEdit, onPreview, onClose, onDelete, onShare],
  );

  const handleSort = (key) => {
    const direction =
      sort?.key === key && sort?.direction === "asc" ? "desc" : "asc";
    onSort?.({ key, direction });
  };

  const handlePageChange = (nextPage) => {
    if (nextPage !== page) onPageChange?.(nextPage);
  };

  const handlePageSizeChange = (nextSize) => {
    if (nextSize !== pageSize) {
      onPageSizeChange?.(nextSize);
      onPageChange?.(1);
    }
  };

  return (
    <DataTable
      columns={columns}
      data={surveys}
      loading={loading}
      sort={sort}
      onSort={handleSort}
      emptyMessage={emptyMessage}
      loadingMessage={loadingMessage}
      getRowKey={(row) => row._id}
      getRowProps={getRowProps}
      pagination={{
        page,
        pageSize,
        total,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
        pageSizeOptions: [5, 10, 25, 50],
        disabled: loading,
        labels: {
          itemsPerPage: t("pagination.itemsPerPage"),
          previous: t("Previous"),
          next: t("Next"),
          pageLabel: ({ page: current, totalPages }) =>
            t("pagination.pageLabel", {
              page: current,
              totalPages,
            }),
        },
      }}
    />
  );
};

export default observer(SurveyLibraryTable);
